import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { ERROR_CODES } from "@nh/shared";
import { ApiError, isApiError } from "@/shared/api/client";

/**
 * Traduce un fallo del API a la frase que ve la persona.
 *
 * Se ramifica sobre `code`, nunca sobre el texto: el mensaje del servidor
 * puede cambiar de redacción o venir en otro idioma, el código no.
 *
 * `INVALID_CREDENTIALS` dice "correo o contraseña" a propósito, sin
 * precisar cuál falló: distinguirlos le regala a quien prueba correos un
 * detector de cuentas existentes.
 */
export function authErrorMessage(error: unknown): string {
  if (!isApiError(error)) {
    return "Algo salió mal. Vuelve a intentarlo.";
  }

  switch (error.code) {
    case ERROR_CODES.INVALID_CREDENTIALS:
      return "El correo o la contraseña no son correctos.";
    case ERROR_CODES.ACCOUNT_LOCKED:
      return "Tu cuenta está bloqueada por demasiados intentos. Inténtalo más tarde o restablece tu contraseña.";
    case ERROR_CODES.EMAIL_TAKEN:
      return "Ese correo ya tiene una cuenta. Entra o usa otro correo.";
    // El login lo intercepta antes de llegar aquí para ofrecer el reenvío
    // (ver `isEmailNotVerified`). Este texto es la red por si el código
    // aparece en otra pantalla: decir solo "algo salió mal" dejaría a
    // alguien reintentando una contraseña que estaba bien.
    case ERROR_CODES.EMAIL_NOT_VERIFIED:
      return "Tu correo todavía no está verificado. Abre el enlace que te mandamos al crear la cuenta.";
    case ERROR_CODES.RATE_LIMITED:
      return "Demasiados intentos seguidos. Espera un momento y vuelve a intentarlo.";
    case ERROR_CODES.VALIDATION_ERROR:
      return "Revisa los datos: hay algo que no cuadra.";
    case ERROR_CODES.TOKEN_EXPIRED:
    case ERROR_CODES.TOKEN_INVALID:
    case ERROR_CODES.UNAUTHENTICATED:
      return "Tu sesión ya no es válida. Vuelve a entrar.";
    case ERROR_CODES.INTERNAL:
      // El caso más común en desarrollo: el backend todavía no está arriba.
      return error.status === 0
        ? "No pudimos contactar al servidor. Revisa tu conexión e inténtalo de nuevo."
        : "El servidor tuvo un problema. Inténtalo de nuevo en un momento.";
    default:
      return error.message || "Algo salió mal. Vuelve a intentarlo.";
  }
}

/**
 * Códigos con los que el servidor rechaza un enlace de correo — el de
 * verificar y el de restablecer la contraseña.
 *
 * El servidor responde LO MISMO si el enlace caducó, si nunca existió o si
 * ya se usó: distinguirlos diría cuáles de los tokens que alguien pruebe
 * fueron reales alguna vez.
 */
export function isEmailLinkRejection(error: unknown): boolean {
  if (!isApiError(error)) return false;
  return (
    error.code === ERROR_CODES.TOKEN_EXPIRED ||
    error.code === ERROR_CODES.TOKEN_INVALID ||
    error.code === ERROR_CODES.NOT_FOUND
  );
}

/**
 * El mensaje para un enlace de correo que el servidor rechazó.
 *
 * NO pasa por la traducción de `authErrorMessage`, y esa es toda la razón
 * de que exista: ahí `TOKEN_EXPIRED`/`TOKEN_INVALID` significan "tu sesión
 * caducó, vuelve a entrar", que es cierto DENTRO de la app pero absurdo
 * aquí — quien abre un enlace del correo no tiene sesión ninguna que
 * hubiera caducado.
 *
 * Se muestra el texto del servidor TAL CUAL. No se adorna con un "parece
 * que expiró" ni con un "quizá ya lo usaste": el servidor contesta igual
 * en los tres casos a propósito, así que este frontend no sabe cuál
 * ocurrió y no puede afirmarlo sin inventárselo.
 *
 * Lo que no es un rechazo de enlace —el servidor caído, la red muerta—
 * sigue cayendo en `authErrorMessage`, que sí tiene algo mejor que decir.
 */
export function emailLinkErrorMessage(error: unknown): string {
  if (isEmailLinkRejection(error) && isApiError(error) && error.message) {
    return error.message;
  }
  return authErrorMessage(error);
}

/**
 * ¿El servidor rechazó la entrada porque el correo no está verificado?
 *
 * Es el único fallo del login que tiene una salida concreta —reenviar el
 * enlace— en vez de un "vuelve a intentarlo". Por eso se reconoce por
 * código en vez de caer en el aviso genérico.
 */
export function isEmailNotVerified(error: unknown): boolean {
  return isApiError(error) && error.code === ERROR_CODES.EMAIL_NOT_VERIFIED;
}

/**
 * Pinta los errores por campo que mandó el servidor (`error.fields`) EN SU
 * CAMPO, no en el aviso de arriba. Un "ese correo ya existe" pertenece al
 * campo de correo: obligar a leer un banner y adivinar cuál de seis campos
 * falló es trabajo que le tocaba a la interfaz.
 *
 * @param allowed campos que existen en ESTE formulario. Un campo que el
 *   servidor manda pero el formulario no tiene se ignora aquí y cae en el
 *   aviso general — pintarlo en la nada lo dejaría invisible.
 * @returns true si al menos un error aterrizó en un campo.
 */
export function applyServerFieldErrors<TValues extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<TValues>,
  allowed: readonly Path<TValues>[],
): boolean {
  if (!(error instanceof ApiError) || !error.fields) return false;

  let applied = false;
  for (const [field, messages] of Object.entries(error.fields)) {
    const path = field as Path<TValues>;
    if (!allowed.includes(path)) continue;
    const message = messages[0];
    if (!message) continue;
    setError(path, { type: "server", message });
    applied = true;
  }
  return applied;
}

/**
 * Un `EMAIL_TAKEN` sin `fields` sigue siendo un problema del correo.
 * Devuelve el campo al que pertenece un código, si pertenece a alguno.
 */
export function fieldForErrorCode(error: unknown): "email" | null {
  if (!isApiError(error)) return null;
  return error.code === ERROR_CODES.EMAIL_TAKEN ? "email" : null;
}
